import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Box, LinearProgress, Typography } from "@mui/material";
import { getPartnerAwareApiUrl, httpClient } from "@/providers/dataProvider/dataProvider";
import { isRetryableError } from "@/config/retryPolicy";
import { ResourceName } from "@/types";
import { formatRichErrorMessage, parseRichError } from "@/utils/errors/richError";

// Format a caught httpClient error the way the rest of the app does — through
// parseRichError → title + actionable detail + support code — instead of dropping to
// the bare top-level `.message`. The cascade backstop returns exactly the shapes this
// exists to format (a 409 with blocked reasons, a structured 5xx), and the old
// useCascadeDelete hook routed failures through useRichApiErrorNotify; the provider must
// not regress that to an impoverished one-line string (#6141 review). Always non-empty,
// but keep an explicit fallback for a truly empty parse.
const richCascadeError = (err: unknown, fallback: string): string =>
  formatRichErrorMessage(parseRichError(err)) || fallback;

/** Lifecycle of a server-side cascade operation (contract with #5753). */
export type CascadeOperationStatus = "pending" | "running" | "completed" | "failed";

/** Body of GET /documents/{id}/cascade-status/{operationId} (#6141 adds progress). */
interface CascadeStatusResponse {
  operation_id: string;
  status: CascadeOperationStatus;
  /** Records processed so far — a live, monotonic count (#6141). */
  progress?: number;
  error?: string;
}

/**
 * Poll cadence and ceiling. At 2s intervals, 90 attempts gives ~3 minutes of
 * headroom for a large multi-record cascade while still failing loudly (rather
 * than polling forever) if the worker wedges. Exported so tests can drive the
 * schedule deterministically with fake timers.
 */
export const CASCADE_POLL_INTERVAL_MS = 2_000;
export const CASCADE_MAX_POLL_ATTEMPTS = 90;

/**
 * How many CONSECUTIVE status-poll rejections we tolerate before treating the
 * cascade as failed. A single flaky 502/timeout over ~90 polls should NOT abort
 * an operation the server is still running; we skip that tick and retry. The
 * counter resets on any successful poll, so only a sustained outage (or the
 * server reporting a terminal `failed`, or the max-attempts timeout) gives up.
 */
export const CASCADE_MAX_CONSECUTIVE_POLL_ERRORS = 3;

/**
 * React-admin resources whose lists/records can contain rows the cascade
 * touched. The raw httpClient DELETE bypasses react-admin's cache, so on a
 * TERMINAL outcome we invalidate these query keys ourselves — otherwise
 * claimants/claims/reimbursements/stop-loss-claims (and the documents list)
 * keep showing deleted rows until their own refetch. React-admin keys queries
 * by resource name, so a prefix invalidation refetches every getOne/getList for
 * that resource. Invalidation — not a caller callback — is the completion side
 * effect BY DESIGN: fire-and-forget means the user may have navigated away, so
 * only mounted queries refetch (unmounted ones just go stale) and nothing force-
 * navigates the user. Exported for the test.
 *
 * We invalidate the query keys directly rather than react-admin's useRefresh()
 * (cf. ADR-040, which mandates cache invalidation after an out-of-band mutation):
 * useRefresh() only works inside a component's render/mutation lifecycle and
 * refreshes ALL mounted queries, whereas this runs from a background provider after
 * the originating dialog has unmounted and targets only the affected resources —
 * the correct tool for a fire-and-forget mutation that outlives its component.
 */
export const CASCADE_INVALIDATED_RESOURCES = [
  ResourceName.DOCUMENTS,
  ResourceName.CLAIMANTS,
  ResourceName.CLAIM_ITEMS,
  ResourceName.REIMBURSEMENTS,
  ResourceName.STOPLOSS_CLAIMS,
] as const;

/** How long a terminal (completed/failed) toast lingers before auto-dismiss. */
const AUTO_DISMISS_SUCCESS_MS = 6_000;
const AUTO_DISMISS_FAILURE_MS = 12_000;

export interface StartCascadeOptions {
  /** Human label for the toast (e.g. the document name). */
  label?: string;
}

interface CascadeDeleteController {
  /**
   * Kick off DELETE ?cascade=true for a document and track it to completion in
   * the background. Returns immediately — the caller closes its dialog / navigates
   * away and the provider owns the poll, the progress toast, and the terminal
   * cache invalidation. Idempotent per document while one is already running
   * (guards a double-trigger).
   *
   * Returns `true` when a real provider accepted the request, `false` from the
   * no-op default (no provider mounted). The caller MUST check this before it
   * navigates away / reports success — a `false` means nothing was started, so a
   * destructive action must not be presented as if it happened.
   */
  start: (documentId: string, options?: StartCascadeOptions) => boolean;
}

// Default is a warn-and-no-op controller, NOT null. The real app always mounts the
// provider in Layout (above every authenticated page), so this default is only ever
// hit by an isolated render — e.g. a unit test mounting the dialog on its own. A
// tolerant default keeps those renders from crashing; returning `false` (not just a
// console warning) lets a real caller detect the missing provider and refuse to fake
// success on a delete that never started (#6141 review).
const NOOP_CONTROLLER: CascadeDeleteController = {
  start: () => {
    console.warn("useCascadeDeleteController used without a CascadeDeleteProvider; cascade delete did not start.");
    return false;
  },
};

const CascadeDeleteContext = createContext<CascadeDeleteController>(NOOP_CONTROLLER);

/** Access the background cascade-delete controller (Layout mounts the provider). */
export const useCascadeDeleteController = (): CascadeDeleteController => useContext(CascadeDeleteContext);

type OpPhase = "running" | "completed" | "failed";

interface CascadeOp {
  /** Local, monotonic UI key (distinct from the server operation id). */
  key: number;
  documentId: string;
  label?: string;
  phase: OpPhase;
  /** Records processed so far (from the poll's progress field). */
  progress?: number;
  /** Terminal message shown on completed/failed. */
  message?: string;
}

/**
 * App-level owner of in-flight document cascade-deletes (#6141 FE). Mounted once,
 * high in the tree (Layout), so an operation's poll OUTLIVES the dialog that
 * started it — the whole point of the fire-and-forget model: the user confirms,
 * the dialog closes, and a background toast reports live progress ("N records
 * processed") until the operation completes or fails.
 *
 * The poll lifecycle (execute → poll → terminal) mirrors the server async contract
 * (#5753): DELETE ?cascade=true → 202 {operation_id}; then GET cascade-status on an
 * interval until completed/failed, with a max-attempts timeout and tolerance for a
 * few transient poll errors. Cancellation is per-op via a live-key set; provider
 * unmount (logout / partner switch) clears every timer and key so no continuation
 * touches state after teardown.
 */
export const CascadeDeleteProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const [ops, setOps] = useState<CascadeOp[]>([]);
  const keyRef = useRef(0);
  // Keys of operations still live. A continuation bails if its key was cleared
  // (provider unmount) so we never setState after teardown.
  const liveRef = useRef<Set<number>>(new Set());
  // Pending timers by op key, so unmount can clear them all.
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  // Documents with an operation currently running — dedupes a double-trigger.
  const runningDocsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const timers = timersRef.current;
    const live = liveRef.current;
    const runningDocs = runningDocsRef.current;
    return () => {
      live.clear();
      runningDocs.clear();
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  const patchOp = useCallback((key: number, patch: Partial<CascadeOp>) => {
    setOps((prev) => prev.map((o) => (o.key === key ? { ...o, ...patch } : o)));
  }, []);

  const removeOp = useCallback((key: number) => {
    setOps((prev) => prev.filter((o) => o.key !== key));
  }, []);

  const scheduleDismiss = useCallback(
    (key: number, ms: number) => {
      const t = setTimeout(() => {
        timersRef.current.delete(key);
        removeOp(key);
      }, ms);
      timersRef.current.set(key, t);
    },
    [removeOp]
  );

  const invalidateAffected = useCallback(() => {
    CASCADE_INVALIDATED_RESOURCES.forEach((resource) => {
      void queryClient.invalidateQueries({ queryKey: [resource] });
    });
  }, [queryClient]);

  const start = useCallback(
    (documentId: string, options?: StartCascadeOptions) => {
      if (runningDocsRef.current.has(documentId)) return true; // already handled (running) — guard double-trigger
      runningDocsRef.current.add(documentId);

      const key = (keyRef.current += 1);
      liveRef.current.add(key);
      setOps((prev) => [...prev, { key, documentId, label: options?.label, phase: "running" }]);

      const isLive = () => liveRef.current.has(key);
      const releaseDoc = () => runningDocsRef.current.delete(documentId);

      const succeed = () => {
        if (!isLive()) return;
        releaseDoc();
        invalidateAffected();
        patchOp(key, { phase: "completed", message: "Document and related records deleted." });
        scheduleDismiss(key, AUTO_DISMISS_SUCCESS_MS);
      };

      const fail = (message: string) => {
        if (!isLive()) return;
        releaseDoc();
        // Partial deletes may have committed before the failure, so refresh the
        // affected lists to reflect reality rather than leaving stale rows.
        invalidateAffected();
        patchOp(key, { phase: "failed", message });
        scheduleDismiss(key, AUTO_DISMISS_FAILURE_MS);
      };

      const scheduleNextPoll = (operationId: string, attempt: number, consecutiveErrors: number) => {
        const t = setTimeout(() => poll(operationId, attempt + 1, consecutiveErrors), CASCADE_POLL_INTERVAL_MS);
        timersRef.current.set(key, t);
      };

      const poll = (operationId: string, attempt: number, consecutiveErrors: number) => {
        if (!isLive()) return;
        if (attempt >= CASCADE_MAX_POLL_ATTEMPTS) {
          fail("The delete is taking longer than expected. It may still complete — refresh in a moment.");
          return;
        }
        httpClient(`${getPartnerAwareApiUrl()}/documents/${documentId}/cascade-status/${operationId}`, {
          method: "GET",
        })
          .then(({ json }: { json: unknown }) => {
            if (!isLive()) return;
            const res = json as CascadeStatusResponse | undefined;
            if (typeof res?.progress === "number") {
              patchOp(key, { progress: res.progress });
            }
            switch (res?.status) {
              case "completed":
                succeed();
                break;
              case "failed":
                fail(res.error || "The delete of related records failed.");
                break;
              default:
                // pending | running | unknown → a successful poll; reset the
                // transient-error streak and schedule the next tick.
                scheduleNextPoll(operationId, attempt, 0);
            }
          })
          .catch((err: unknown) => {
            if (!isLive()) return;
            // #3804: only TRANSIENT failures may consume the tolerance budget.
            // A 403/404 (revoked permission, unknown operation id) will return
            // the same answer on every one of the remaining ~90 attempts, so
            // spending 3 ticks (~6s) before surfacing it is pure latency.
            //
            // 401 is exempted because this loop re-acquires a token per attempt
            // (httpClient → getAccessToken) with no checkError-driven refresh of
            // its own. Treating it as terminal would abort on tick 1 after a
            // single token blip — and `fail()` both invalidates the affected
            // caches and shows a failure message, so the user would be told the
            // delete failed and then watch the rows vanish, because the server
            // had in fact committed it. (Not an ordering bug: `invalidateAffected`
            // is fire-and-forget `void invalidateQueries` and `patchOp` is a
            // batched setState, so the message always renders before the refetch
            // lands. Swapping the two lines would change nothing.)
            if (!isRetryableError(err, { retryUnauthorized: true })) {
              fail(richCascadeError(err, "Lost contact while checking the delete status."));
              return;
            }
            const nextConsecutive = consecutiveErrors + 1;
            if (nextConsecutive >= CASCADE_MAX_CONSECUTIVE_POLL_ERRORS) {
              fail(richCascadeError(err, "Lost contact while checking the delete status."));
              return;
            }
            scheduleNextPoll(operationId, attempt, nextConsecutive);
          });
      };

      httpClient(`${getPartnerAwareApiUrl()}/documents/${documentId}?cascade=true`, { method: "DELETE" })
        .then(({ json }: { json: unknown }) => {
          if (!isLive()) return;
          const operationId = (json as { operation_id?: string })?.operation_id;
          if (!operationId) {
            fail("The delete could not be started. Please try again.");
            return;
          }
          poll(operationId, 0, 0);
        })
        .catch((err: unknown) => {
          fail(richCascadeError(err, "The delete could not be started. Please try again."));
        });

      return true;
    },
    [invalidateAffected, patchOp, scheduleDismiss]
  );

  const controller = useMemo<CascadeDeleteController>(() => ({ start }), [start]);

  return (
    <CascadeDeleteContext.Provider value={controller}>
      {children}
      {ops.length > 0 && (
        <Box
          role="status"
          aria-live="polite"
          sx={{
            // Bottom-RIGHT: the usual toast corner, and it clears the left nav rail
            // (which otherwise overlapped/occluded a bottom-left toast). zIndex.tooltip
            // sits above the drawer/app-bar so the toast is never rendered behind the nav.
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: (t) => t.zIndex.tooltip,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            maxWidth: 380,
            width: "calc(100% - 48px)",
            pointerEvents: "none",
          }}
        >
          {ops.map((op) => (
            <Alert
              key={op.key}
              severity={op.phase === "completed" ? "success" : op.phase === "failed" ? "error" : "info"}
              onClose={op.phase === "running" ? undefined : () => removeOp(op.key)}
              sx={{ boxShadow: 3, width: "100%", pointerEvents: "auto" }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {op.phase === "completed"
                  ? "Delete complete"
                  : op.phase === "failed"
                    ? "Delete failed"
                    : "Deleting related records…"}
              </Typography>
              {op.label && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {op.label}
                </Typography>
              )}
              {op.phase === "running" ? (
                <Box sx={{ mt: 0.5 }}>
                  <LinearProgress variant="indeterminate" />
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    {typeof op.progress === "number"
                      ? `${op.progress} record${op.progress === 1 ? "" : "s"} processed`
                      : "Starting…"}
                  </Typography>
                </Box>
              ) : (
                op.message && (
                  // pre-line so a rich failure (title + detail + support code, newline-
                  // separated from formatRichErrorMessage) renders on separate lines.
                  <Typography variant="caption" sx={{ display: "block", whiteSpace: "pre-line" }}>
                    {op.message}
                  </Typography>
                )
              )}
            </Alert>
          ))}
        </Box>
      )}
    </CascadeDeleteContext.Provider>
  );
};
